import { useListState } from "@/hooks/use-list-state";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, throwIfResNotOk } from "@/lib/queryClient";
import { deriveSearchWords, filterInboxRows } from "@/lib/inbox-search";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { NewMessageModal } from "@/components/new-message-modal";
import { MessageWholeHouseModal } from "@/components/message-whole-house-modal";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { PropertyMultiSelectFilter } from "@/components/property-multi-select-filter";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AutoResolveDialog, AUTO_RESOLVE_SETTINGS_Q_KEY } from "./app-messages/AutoResolveDialog";
import { AlertCircle, MessageSquare, MessageSquareText, Plug, Plus, RotateCcw, ScrollText, Settings, Sparkles, Users } from "lucide-react";
import { useBetaAccess } from "@/hooks/use-beta-access";
import { AUTO_DRAFT_BETA } from "@shared/private-property-betas";
import type { Property } from "@shared/schema";
import type { Tab } from "@/lib/messages-tabs";
import { resolveTab } from "@/lib/messages-tabs";
import { usePageTitle } from "@/hooks/use-page-title";
import { InboxRow, LAST_MEMBER_ID_KEY } from "./app-messages/_shared";
import { INBOX_UNRESOLVED_COUNT_Q_KEY } from "./app-messages/_resolve-invalidations";
import { InboxTab } from "./app-messages/InboxTab";
import { PendingTab } from "./app-messages/PendingTab";
import { WaitingRepliesTab } from "./app-messages/WaitingRepliesTab";
import { RulesTab } from "./app-messages/RulesTab";
import { CannedResponsesTab } from "./app-messages/CannedResponsesTab";
import { HouseChatsTab } from "./app-messages/HouseChatsTab";
import { useHouseChats } from "./app-messages/useHouseChats";
export type { TimelineChat } from "./app-messages/_shared";
export { ChatBubble } from "./app-messages/ChatBubble";
export { MemberTimeline } from "./app-messages/MemberTimeline";
export { MemberInfoSidebar } from "./app-messages/MemberInfoSidebar";
export { CannedResponsesTab } from "./app-messages/CannedResponsesTab";

// Inbox infinite scroll — how many conversations each server fetch covers and
// the hard ceiling (mirrors the route's Math.min(limit, 500) cap). Past the
// ceiling the sidebar shows the "use search" hint instead of loading more.
import { BroadcastDeepLinkDialog } from "@/components/broadcast-status-dialog";
const INBOX_FETCH_STEP = 100;
const INBOX_FETCH_MAX = 500;

// ─── Main page component ──────────────────────────────────────────────────────
export default function AppMessages() {
  usePageTitle("Messages");
  const queryClient = useQueryClient();
  const [location, navigate] = useLocation();
  // wouter's useLocation only reflects the path, not the search string.
  // Subscribing to the search string lets the tab-sync and deep-link reader
  // effects below also re-fire when only the query changes (e.g. dashboard
  // notification updating ?member=N&chat=M while /app/messages is mounted).
  // Renamed to avoid colliding with the inbox-search useState below.
  const urlSearch = useSearch();
  const { canEdit, canView } = usePermissions();
  const canSend = canEdit("messages");
  const canManage = canEdit("messages");
  const canAccessMessages = canView("messages");
  // Quo messaging — the Messaging provider settings entry. `quo_messaging` is
  // generally available (shared/beta-ga.ts, D22), so this is true for everyone.
  const hasQuoMessaging = useBetaAccess("quo_messaging");
  // Messages Auto Draft — private beta; the menu item is invisible without the grant.
  const hasAutoDraft = useBetaAccess(AUTO_DRAFT_BETA.key);
  const { toast } = useToast();

  // Resolve active tab from URL reactively via shared hook; alias resolution
  // ("drafts" → "awaiting") is preserved by piping through resolveTab.
  // tab-allow-list-exempt: legacy "drafts" alias is resolved to "awaiting" via
  // resolveTab(), so the hook intentionally reads the raw value here.
  const [_rawTab, _setTabParam] = useTabQueryParam("tab", "inbox", { replace: true });
  const activeTab: Tab = resolveTab(_rawTab);
  const isInboxActive = activeTab === "inbox";

  function switchTab(tab: Tab) {
    _setTabParam(tab);
  }

  function selectMember(id: number | null, focusChatId?: number | null, openInbox = false) {
    setSelectedMemberId(id);
    const params = new URLSearchParams(window.location.search);
    if (String(id) !== (params.get("memberId") ?? params.get("member"))) {
      params.delete("cleaningProperty");
      params.delete("cleaningAssignment");
    }
    if (openInbox) params.set("tab", "inbox");
    if (id != null) {
      params.set("memberId", String(id));
      // Explicit in-app selection opens EXACTLY this stay row's thread. The
      // marker suppresses the active-stay deep-link resolution below so a
      // host auditing a closed stay (via show-inactive or search) isn't
      // bounced to the person's housed successor — "conversation follows the
      // person" is for external links (team inbox, dashboard, notifications),
      // not manual card clicks. Living in the URL, it also survives browser
      // back/forward through manually opened threads.
      params.set("exact", "1");
      // Drop legacy deep-link params that are no longer relevant when the
      // user explicitly picks a different conversation. If a focusChatId was
      // supplied (e.g. from clicking a content-search match), set ?chat= so
      // the existing deep-link infra scrolls to + highlights that bubble.
      params.delete("member");
      params.delete("thread");
      // Also drop ?pending= — it was never cleared here, so a failed-send deep
      // link left focusPendingId set for the rest of the session.
      params.delete("pending");
      if (focusChatId != null) {
        params.set("chat", String(focusChatId));
      } else {
        params.delete("chat");
      }
      try {
        sessionStorage.setItem(LAST_MEMBER_ID_KEY, String(id));
      } catch {}
    } else {
      params.delete("memberId");
      params.delete("member");
      params.delete("chat");
      params.delete("thread");
      params.delete("exact");
      try {
        sessionStorage.removeItem(LAST_MEMBER_ID_KEY);
      } catch {}
    }
    // Use push (not replace) so each conversation selection becomes a
    // separate history entry — enabling browser back/forward to move
    // between previously viewed conversations.
    navigate(`/app/messages?${params.toString()}`);
  }

  // Tab↔URL sync is now handled reactively by useTabQueryParam above.
  // "drafts" alias is resolved to "awaiting" via resolveTab on every render.

  // Deep-link ?memberId= from member detail page, OR ?member=N&chat=M from
  // dashboard notifications. The chat id is forwarded to ChatBubble via the
  // `focusChatId` prop so the right bubble gets scrolled into view + briefly
  // highlighted. Re-runs on every location change so a click from the
  // notifications panel (which uses wouter's navigate) updates the open
  // thread even if the user is already on /app/messages.
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  // On first mount, if no deep-link memberId is present in the URL, restore
  // the last conversation the host had open (persisted in sessionStorage).
  // Deep-link params (?memberId=, ?member=) take precedence so links from
  // notifications / member detail still navigate to the intended thread.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("memberId") ||
      params.get("member") ||
      params.get("chat") ||
      params.get("thread") ||
      params.get("pending")
    ) return;
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(LAST_MEMBER_ID_KEY);
    } catch {}
    if (stored && /^\d+$/.test(stored)) {
      params.set("memberId", stored);
      navigate(`/app/messages?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [focusChatId, setFocusChatId] = useState<number | null>(null);
  const [focusThreadId, setFocusThreadId] = useState<number | null>(null);
  // PadSplit send-UX — deep-link target for a failed OUTBOUND row (a
  // messages_pending id, not a delivered member_chats id). Every failed-send
  // surface (bell / Team Inbox note / conversation-list indicator) links with
  // ?pending=P so click-to-open lands on the exact failed bubble.
  const [focusPendingId, setFocusPendingId] = useState<number | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get("memberId") ?? params.get("member");
    // URL is authoritative: set or clear selection so browser back/forward
    // (which changes the URL without triggering a click handler) restores the
    // correct conversation — including deselecting when memberId is absent.
    if (m && /^\d+$/.test(m)) {
      setSelectedMemberId(parseInt(m, 10));
    } else {
      setSelectedMemberId(null);
    }
    const c = params.get("chat");
    if (c && /^\d+$/.test(c)) setFocusChatId(parseInt(c, 10));
    else setFocusChatId(null);
    const t = params.get("thread");
    if (t && /^\d+$/.test(t)) setFocusThreadId(parseInt(t, 10));
    else setFocusThreadId(null);
    const p = params.get("pending");
    if (p && /^\d+$/.test(p)) setFocusPendingId(parseInt(p, 10));
    else setFocusPendingId(null);
    // Task #2814 — Opening a thread-note mention deep link clears the matching
    // bell notification (covers fresh loads, an already-mounted page, and
    // non-bell entry like a direct URL or refresh). Best-effort.
    if (t && /^\d+$/.test(t)) {
      apiRequest("PATCH", "/api/app/notifications/read-mention", { threadId: parseInt(t, 10) })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
        })
        .catch(() => {});
    }
    // Active-stay precedence (room-transfer conversation bug, member 1375):
    // deep links carry the member row id straight off a chat row, which under
    // stay grain can be a CLOSED stay whose conversation the repoint sweep
    // moved onto the person's housed successor. Ask the server for the row the
    // conversation actually lives on and rewrite the URL to it (replace, not
    // push — the superseded target shouldn't survive in history). The server
    // returns the requested id unchanged for housed rows, ambiguous
    // mid-transfer people (Model B), and anything outside the session's scope,
    // so this settles in one rewrite. Best-effort: on any failure the
    // requested thread just opens as before.
    //
    // EXTERNAL deep links only: ?exact=1 (set by selectMember card clicks and
    // the member-detail "Open in Messages" surfaces) means the host explicitly
    // chose this stay row — open it as-is so closed stays stay manually
    // auditable. External surfaces (team inbox, dashboard chat links,
    // notifications, emails) never mint the marker, so they still follow the
    // person. Skipping also saves the resolution round trip on every in-app
    // conversation click.
    let staleResolution = false;
    if (m && /^\d+$/.test(m) && params.get("exact") !== "1") {
      const requested = parseInt(m, 10);
      apiRequest("GET", `/api/app/members/${requested}/conversation-target`)
        .then((r) => r.json())
        .then((data: { memberId?: number }) => {
          if (staleResolution) return;
          const resolved = Number(data?.memberId);
          if (!Number.isFinite(resolved) || resolved <= 0 || resolved === requested) return;
          const next = new URLSearchParams(window.location.search);
          // Normalize onto ?memberId= (the canonical param) whichever alias
          // carried the superseded id.
          next.delete("member");
          next.set("memberId", String(resolved));
          try {
            sessionStorage.setItem(LAST_MEMBER_ID_KEY, String(resolved));
          } catch {}
          navigate(`/app/messages?${next.toString()}`, { replace: true });
        })
        .catch(() => {});
    }
    return () => {
      staleResolution = true;
    };
  }, [location, urlSearch]);

  // Search and property group filter
  const [search, setSearch] = useListState("messages:search", "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  // Per-word message-history match sets from the server. null = not yet fetched
  // for the current query. Combined with in-memory field matches client-side to
  // implement combined-corpus AND: each word must match either the in-memory
  // fields OR the server content result for that word.
  const [contentMatchIdsByWord, setContentMatchIdsByWord] = useState<Record<string, Set<number>> | null>(null);
  // memberId → most recent member_chats.id whose text matched any query word.
  // When the host clicks a search-matched member, we navigate with ?chat=<id>
  // so the existing focusChatId infra scrolls + highlights the matched bubble
  // (with bounded lookback for old messages outside the most-recent page).
  const [matchedChatIdByMember, setMatchedChatIdByMember] = useState<Record<number, number>>({});
  // Task #2075 — memberId → short excerpt of the matched message (centered
  // on the first matched word, with optional leading/trailing "…"). Used by
  // the inbox row to render the matched snippet in place of lastChatPreview
  // when the row was matched by message-content search.
  const [matchedSnippetByMember, setMatchedSnippetByMember] = useState<Record<number, string>>({});
  // Task #4574 — enriched InboxRows the server built for members matched by
  // search (name OR content) that fall OUTSIDE the default top-100 recency page.
  // The client only otherwise filters rows it already loaded, so without these
  // an out-of-window member (e.g. a low-recency thread) can never render even
  // though the server found it. Merged into the base list in the filtered memo.
  const [searchMatchedRows, setSearchMatchedRows] = useState<InboxRow[]>([]);
  const [isContentSearching, setIsContentSearching] = useState(false);
  // Search mode: "people" = name/property in-memory match only (default, avoids
  // the "trip out" problem where a name like "Cole" highlights 78 thread messages);
  // "messages" = existing combined-corpus keyword search with in-thread navigator.
  // Persisted in localStorage (not sessionStorage) so the host's chosen search
  // mode survives browser restarts; first-time users (no stored value) still
  // default to "people". (Task #3658)
  const SEARCH_MODE_STORAGE_KEY = "padpulse:inbox:searchMode";
  const [searchMode, setSearchMode] = useState<"people" | "messages">(() => {
    if (typeof window === "undefined") return "people";
    const stored = window.localStorage.getItem(SEARCH_MODE_STORAGE_KEY);
    return stored === "messages" ? "messages" : "people";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SEARCH_MODE_STORAGE_KEY, searchMode);
  }, [searchMode]);
  const [groupFilter, setGroupFilter] = useListState("messages:group", "all");
  const [propertyFilter, setPropertyFilter] = useListState<number[]>("messages:properties", []);
  // Client-side toggle (Task #2994) — when on, narrow the inbox list to only
  // members with at least one unresolved message (unresolvedCount > 0). Works
  // alongside search, tab, group, and property filters; no API change needed.
  // Persisted in sessionStorage (mirrors the inbox page pattern in InboxTab) so
  // a host working through their open queue doesn't have to re-enable it on
  // every reload or navigation back to the inbox.
  const UNRESOLVED_ONLY_STORAGE_KEY = "padpulse:inbox:unresolvedOnly";
  const [unresolvedOnly, setUnresolvedOnly] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.sessionStorage.getItem(UNRESOLVED_ONLY_STORAGE_KEY);
    // Default to ON (true) when no explicit preference has been saved.
    return stored === null ? true : stored === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      UNRESOLVED_ONLY_STORAGE_KEY,
      unresolvedOnly ? "1" : "0",
    );
  }, [unresolvedOnly]);
  // Time-window filter for message-content search. Persists while a search is
  // active; resets to "all" automatically when the search query is cleared.
  const [timeFilter, setTimeFilter] = useListState<"7d" | "30d" | "90d" | "all">("messages:time", "all");
  // Derive the epoch-ms cutoff from timeFilter for passing to InboxTab /
  // MemberTimeline so the timeline match-navigator also respects the window.
  const searchSinceMs = useMemo<number | null>(() => {
    if (timeFilter === "all") return null;
    const days = timeFilter === "7d" ? 7 : timeFilter === "30d" ? 30 : 90;
    return Date.now() - days * 24 * 60 * 60 * 1000;
  }, [timeFilter]);

  // Modal states
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  // Auto-resolve old messages dialog — reachable from Settings menu and
  // auto-opens on first visit when no cutoff is set and prompt not dismissed.
  const [autoResolveOpen, setAutoResolveOpen] = useState(false);
  // true when the auto-open fires (first-visit), false when host opens it manually.
  const [autoResolveIsFirstVisit, setAutoResolveIsFirstVisit] = useState(false);

  // Auto-resolve settings — fetched once per page mount to decide whether
  // the first-visit prompt should auto-open. Fails closed (dialog never auto-
  // opens on error or while loading) so a network blip never nags the host.
  const autoResolveQuery = useQuery<{ cutoff: string | null; promptDismissed: boolean }>({
    queryKey: AUTO_RESOLVE_SETTINGS_Q_KEY,
    staleTime: 5 * 60 * 1000,
  });
  // First-visit auto-open: fires once when the data arrives and the host
  // hasn't set a cutoff and hasn't dismissed the prompt.
  useEffect(() => {
    if (autoResolveQuery.isLoading || autoResolveQuery.isError) return;
    const data = autoResolveQuery.data;
    if (!data) return;
    if (!data.cutoff && !data.promptDismissed) {
      setAutoResolveIsFirstVisit(true);
      setAutoResolveOpen(true);
    }
    // Only run once per mount (data identity changes at most once on first fetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResolveQuery.isLoading]);

  // Show Inactive Members — read from profile, persisted via PATCH.
  const profileQuery = useQuery<{ showInactiveMessages?: boolean }>({
    queryKey: ["/api/app/profile"],
    staleTime: 5 * 60 * 1000,
    enabled: canAccessMessages,
  });
  const showInactiveMembers = profileQuery.data?.showInactiveMessages === true;
  const profileMutation = useMutation({
    mutationFn: async (nextShowInactive: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", {
        showInactiveMessages: nextShowInactive,
      });
      return res.json();
    },
    meta: { silentSave: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox", { unresolvedOnly: 1 }] });
    },
    onError: (err: unknown) => {
      toast({
        description: parseErrorMessage(err, "Failed to update inactive members setting."),
        variant: "destructive",
      });
    },
  });

  // Unmatched SMS count (for triage banner)
  const unmatchedCountQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/unmatched-sms/count"],
    refetchInterval: 60000,
  });
  const unmatchedCount = unmatchedCountQuery.data?.count ?? 0;

  // Properties (for group filter)
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });

  // Inbox data. Poll every 10s so newly arrived conversations, updated
  // previews, and the per-member "N open" badges surface on their own without a
  // manual refresh. refetchIntervalInBackground stays false so polling pauses
  // while the browser tab is hidden — saving network/server load while the host
  // is on another tab. staleTime 0 + refetchOnMount "always" defeats the global
  // queryClient staleTime:Infinity default (otherwise a freshly-arrived inbound
  // bumps the unread badge but the conversation row itself doesn't move into the
  // thread list until a hard refresh); refetchOnWindowFocus catches activity
  // that happened while the host was on another tab/window.
  // Infinite scroll — the fetch limit starts at one server page and grows as
  // the host scrolls past the loaded conversations (InboxTab's sentinel calls
  // requestMoreInbox). A single query with a growing ?limit= (rather than
  // stacked useInfiniteQuery pages) keeps the 10s poll to ONE request per
  // cycle: the server computes the full inbox before slicing anyway, so each
  // deeper fetch costs the same as page one and only the payload grows.
  // The property filter must show EVERYBODY at the selected properties.
  // The recency-paged inbox above only covers conversations inside the loaded
  // window, so filtering it client-side surfaces just the members who happen
  // to fall in that window. When a property (or group) filter is active we
  // fetch a property-scoped list from the server instead — it returns every
  // visible member whose current property matches, regardless of recency.
  const scopedPropertyIds = useMemo<number[] | null>(() => {
    if (propertyFilter.length > 0) return [...propertyFilter].sort((a, b) => a - b);
    if (groupFilter !== "all") {
      return (propertiesQuery.data ?? [])
        .filter((p) => p.propertyGroup === groupFilter)
        .map((p) => p.id)
        .sort((a, b) => a - b);
    }
    return null;
  }, [propertyFilter, groupFilter, propertiesQuery.data]);

  // True when another query owns the conversation list (the property-scoped
  // one, or the unresolved one). The recency query pauses polling then — two
  // full inbox computations per 10s cycle with one discarded is pure waste.
  const inboxPollOwnedByOther =
    unresolvedOnly || (scopedPropertyIds != null && scopedPropertyIds.length > 0);
  const [inboxLimit, setInboxLimit] = useState(INBOX_FETCH_STEP);
  const inboxQuery = useQuery<InboxRow[]>({
    queryKey: ["/api/app/inbox", { limit: inboxLimit }],
    queryFn: async () => {
      const res = await fetch(`/api/app/inbox?limit=${inboxLimit}`, { credentials: "include" });
      // Shared handler, not a bare throw: this is the page's 10s poller and so
      // usually the first query to observe a revoked session / upgrade gate.
      await throwIfResNotOk(res);
      return res.json();
    },
    // Keep the previous page rendered while a deeper fetch is in flight so
    // growing the limit never blanks the list.
    placeholderData: (prev) => prev,
    // Don't poll the recency page when another query owns the list. Under a
    // property/group filter the scoped query supplies the rows, and under the
    // unresolved filter the unresolved query does — leaving this one running
    // meant TWO full inbox computations every 10s, one of them discarded.
    // Keep cached rows as the fallback until the owning query lands, but
    // disable all automatic requests while another message tab is visible.
    // staleTime: 0 makes re-enabling fetch immediately on return to Inbox.
    enabled: isInboxActive,
    refetchInterval: inboxPollOwnedByOther ? false : 10000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });
  const inboxLoadedCount = inboxQuery.data?.length ?? 0;
  // Handing the list BACK to the recency query must not leave it stale:
  // TanStack restarts the interval when refetchInterval flips false→10000 but
  // does not fetch immediately, so the host would otherwise see rows frozen
  // from the moment the filter went on for up to a full tick.
  const prevInboxPollOwnedRef = useRef(inboxPollOwnedByOther);
  useEffect(() => {
    const wasOwned = prevInboxPollOwnedRef.current;
    prevInboxPollOwnedRef.current = inboxPollOwnedByOther;
    if (isInboxActive && wasOwned && !inboxPollOwnedByOther) void inboxQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxPollOwnedByOther, isInboxActive]);
  // A full page means the server likely holds older conversations.
  const inboxServerHasMore = inboxLoadedCount >= inboxLimit && inboxLimit < INBOX_FETCH_MAX;
  const inboxServerCapReached = inboxLimit >= INBOX_FETCH_MAX && inboxLoadedCount >= INBOX_FETCH_MAX;
  // Grow the fetch limit one step — but only once the current limit is
  // actually filled, so repeated sentinel fires while a fetch is in flight
  // can't run the limit away to the cap.
  const inboxLoadedCountRef = useRef(0);
  inboxLoadedCountRef.current = inboxLoadedCount;
  const requestMoreInbox = useCallback(() => {
    setInboxLimit((l) =>
      inboxLoadedCountRef.current >= l ? Math.min(l + INBOX_FETCH_STEP, INBOX_FETCH_MAX) : l,
    );
  }, []);
  const propertyScopedInboxQuery = useQuery<InboxRow[]>({
    queryKey: ["/api/app/inbox", { propertyIds: scopedPropertyIds ?? [] }],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/inbox?propertyIds=${(scopedPropertyIds ?? []).join(",")}`,
        { credentials: "include" },
      );
      await throwIfResNotOk(res);
      return res.json();
    },
    // Not while the unresolved filter is on — that query owns the list then,
    // and `filtered` explicitly ignores the scoped rows, so this would be a
    // 500-row fetch every 10s whose result is never read.
    enabled: isInboxActive && !unresolvedOnly && scopedPropertyIds != null && scopedPropertyIds.length > 0,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });
  // Task #4325 — server-side unresolved inbox. When the "Unresolved messages
  // only" toggle is on, we fetch from a separate endpoint variant that returns
  // ALL members with open inbound messages (not just the top-100 by recency).
  // The regular inboxQuery stays active so the timeline and member-info sidebar
  // can resolve members outside the unresolved set (e.g. the currently-open
  // conversation after the host resolves a message).
  const unresolvedInboxQuery = useQuery<InboxRow[]>({
    queryKey: ["/api/app/inbox?unresolvedOnly=1"],
    enabled: isInboxActive && unresolvedOnly,
    refetchInterval: unresolvedOnly ? 10000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: unresolvedOnly,
    refetchOnMount: unresolvedOnly ? "always" : false,
    staleTime: 0,
  });
  // Lightweight count of members with open messages. Enabled throughout Inbox
  // so the toggle shows the count even before the filter is turned on.
  // Hits a count-only endpoint variant so it never pulls the full unresolved
  // member list. The key (INBOX_UNRESOLVED_COUNT_Q_KEY) is invalidated by the
  // shared resolve/unresolve path (invalidateResolveQueries) so the count stays
  // live immediately on resolve/unresolve, not just on the polling interval.
  const unresolvedCountQuery = useQuery<{ count: number }>({
    queryKey: INBOX_UNRESOLVED_COUNT_Q_KEY,
    enabled: isInboxActive,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  // While the filter is ON we already have the exact list, so use its length;
  // otherwise fall back to the count-only query.
  const unresolvedTotal = unresolvedOnly
    ? (unresolvedInboxQuery.data?.length ?? unresolvedCountQuery.data?.count ?? null)
    : (unresolvedCountQuery.data?.count ?? null);

  // Task #2071 — debounce the search input so we don't fire a request on
  // every keystroke. 400 ms is snappy but avoids hammering the server.
  useEffect(() => {
    if (!isInboxActive) return;
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search, isInboxActive]);

  // Task #2071 — when the debounced query changes, hit the server for per-word
  // message-history member ID sets. Combined with in-memory field matches in
  // the filtered memo to get true combined-corpus AND semantics.
  // Only fires in "messages" mode; in "people" mode the inbox is filtered
  // purely by in-memory fields (name / property / preview) so the host can
  // find a conversation without triggering in-thread keyword highlighting.
  useEffect(() => {
    if (!isInboxActive) {
      setIsContentSearching(false);
      return;
    }
    // On return, let the debounce catch up before searching if the host left
    // Inbox mid-keystroke. Retain search results while the Inbox is hidden.
    if (debouncedSearch !== search) return;
    const q = debouncedSearch.trim();
    if (!q) {
      setContentMatchIdsByWord(null);
      setMatchedChatIdByMember({});
      setMatchedSnippetByMember({});
      setSearchMatchedRows([]);
      setIsContentSearching(false);
      // Reset the time-window filter so the next search always starts at "All time".
      setTimeFilter("all");
      return;
    }
    // Task #4574 — the search endpoint now also returns enriched InboxRows for
    // out-of-recency-window matches (name + content), so we hit it in BOTH
    // "people" and "messages" modes. In people mode we only consume matchedRows
    // (name matches) and skip content-highlighting state so the mode's
    // in-memory-only match semantics are preserved.
    const isMessages = searchMode === "messages";
    let cancelled = false;
    const controller = new AbortController();
    // Reset stale per-word sets immediately so prior query doesn't bleed through.
    setContentMatchIdsByWord(null);
    setMatchedChatIdByMember({});
    setMatchedSnippetByMember({});
    setSearchMatchedRows([]);
    if (isMessages) setIsContentSearching(true);
    // Build the search URL; include the `since` epoch-ms param when the user
    // has selected a time window (7 / 30 / 90 days).
    const days = timeFilter === "7d" ? 7 : timeFilter === "30d" ? 30 : timeFilter === "90d" ? 90 : null;
    const sinceMs = days != null ? Date.now() - days * 24 * 60 * 60 * 1000 : null;
    const searchParams = new URLSearchParams({ q });
    if (sinceMs != null) searchParams.set("since", String(sinceMs));
    fetch(`/api/app/inbox/search?${searchParams.toString()}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data: {
        memberIdsByWord: Record<string, number[]>;
        matchedChatIdByMember: Record<string, number>;
        matchedSnippetByMember?: Record<string, string>;
        matchedRows?: InboxRow[];
      }) => {
        if (cancelled) return;
        // Enriched out-of-window rows apply in both modes (fixes name search
        // for members below the recency page).
        setSearchMatchedRows(Array.isArray(data.matchedRows) ? data.matchedRows : []);
        if (!isMessages) return;
        const byWord: Record<string, Set<number>> = {};
        for (const [word, ids] of Object.entries(data.memberIdsByWord ?? {})) {
          byWord[word] = new Set(ids);
        }
        setContentMatchIdsByWord(byWord);
        // JSON object keys are strings; coerce to numeric memberIds.
        const byMember: Record<number, number> = {};
        for (const [k, v] of Object.entries(data.matchedChatIdByMember ?? {})) {
          byMember[Number(k)] = Number(v);
        }
        setMatchedChatIdByMember(byMember);
        const snipByMember: Record<number, string> = {};
        for (const [k, v] of Object.entries(data.matchedSnippetByMember ?? {})) {
          snipByMember[Number(k)] = String(v);
        }
        setMatchedSnippetByMember(snipByMember);
      })
      .catch(() => {
        if (!cancelled) {
          setContentMatchIdsByWord({});
          setMatchedChatIdByMember({});
          setMatchedSnippetByMember({});
          setSearchMatchedRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsContentSearching(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debouncedSearch, search, timeFilter, searchMode, isInboxActive]);

  const filtered = useMemo(() => {
    // Task #4325 — when unresolvedOnly is on, base the filtered list on the
    // server-side unresolved query (all open threads, not just the top-100 by
    // recency). The client-side unresolvedCount > 0 filter is skipped because
    // the server already guarantees every returned row has an open message.
    // Property-scoped rows (all members at the filtered properties) replace
    // the recency page as the base while the property/group filter is active,
    // so the list shows the WHOLE house — not just members inside the loaded
    // window. Falls back to the recency page until the scoped fetch lands.
    const propertyScopedRows =
      !unresolvedOnly && scopedPropertyIds != null && scopedPropertyIds.length > 0
        ? propertyScopedInboxQuery.data
        : null;
    const baseRows = unresolvedOnly
      ? (unresolvedInboxQuery.data ?? [])
      : (propertyScopedRows ?? inboxQuery.data ?? []);
    // Task #4574 — while a search is active, merge in the server-built enriched
    // rows for matched members that fall OUTSIDE the loaded recency page (deduped
    // by memberId, keeping the already-loaded row). This lets both people-mode
    // (in-memory name match on the injected row) and messages-mode surface an
    // out-of-window member the server found.
    let rows = baseRows;
    if (search.trim() && searchMatchedRows.length > 0) {
      const seen = new Set(baseRows.map((r) => r.memberId));
      const extra = searchMatchedRows.filter((r) => !seen.has(r.memberId));
      if (extra.length > 0) rows = [...baseRows, ...extra];
    }
    let result = rows;
    if (search.trim()) {
      // See client/src/lib/inbox-search.ts for the combined-corpus AND matching
      // and ranking model (extracted there so it can be unit-tested directly).
      // In "people" mode pass null so only in-memory fields (name/property/preview)
      // satisfy the match — the server content corpus is excluded entirely.
      const words = deriveSearchWords(search);
      result = filterInboxRows(result, words, searchMode === "people" ? null : contentMatchIdsByWord);
    }
    if (groupFilter !== "all") {
      const properties = propertiesQuery.data ?? [];
      const groupPropertyIds = new Set(
        properties.filter((p) => p.propertyGroup === groupFilter).map((p) => p.id),
      );
      result = result.filter((r) => r.propertyId != null && groupPropertyIds.has(r.propertyId));
    }
    if (propertyFilter.length > 0) {
      const ids = new Set(propertyFilter);
      result = result.filter((r) => r.propertyId != null && ids.has(r.propertyId));
    }
    return result;
  }, [inboxQuery.data, unresolvedInboxQuery.data, propertyScopedInboxQuery.data, scopedPropertyIds, search, searchMode, contentMatchIdsByWord, searchMatchedRows, groupFilter, propertyFilter, unresolvedOnly, propertiesQuery.data]);

  // House chats power the "House Chats" tab: the badge counts unread member
  // messages across all direct properties, and the tab hides entirely for
  // PadSplit-only hosts (the endpoint returns [] when there are no self_managed
  // properties). Shares one query/poll with the tab body via the query key.
  const houseChatsQuery = useHouseChats();
  const houseChatsUnread = (houseChatsQuery.data ?? []).reduce((sum, c) => sum + c.unreadCount, 0);
  // Show while loading (avoids a flash for the self-managed majority); hide only
  // once we've confirmed the host has no direct properties.
  const showHouseChats = houseChatsQuery.data == null || houseChatsQuery.data.length > 0;

  // Tab id "awaiting" is kept (deep links + dashboard badge use it); ?tab=drafts alias preserved.
  const TAB_ITEMS: { id: Tab; label: string; badge?: number }[] = [
    { id: "inbox", label: "Inbox" },
    ...(showHouseChats ? [{ id: "houseChats" as Tab, label: "House Chats", badge: houseChatsUnread }] : []),
    { id: "awaiting", label: "Drafts" },
    { id: "waitingReplies", label: "Awaiting Replies" },
    { id: "scheduled", label: "Scheduled" },
  ];

  return (
    <AppLayout scrollLocked>
      <div className="flex h-full flex-col" data-testid="page-messages">
        {/* ── Page header with tabs + action buttons ── */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3" data-testid="messages-header">
            <div className="flex min-w-0 flex-1 basis-full flex-wrap items-center gap-2 sm:basis-auto" data-testid="messages-header-controls">
              <div className="flex shrink-0 items-center gap-3">
                <MessageSquare className="h-5 w-5 shrink-0 text-gray-600" />
                <h1 className="text-lg font-semibold text-gray-900" data-tour="messages-heading">Messages</h1>
              </div>
              <PropertyGroupFilter
                properties={propertiesQuery.data ?? []}
                value={groupFilter}
                onChange={setGroupFilter}
                className="w-auto max-w-full sm:w-[180px]"
              />
              <PropertyMultiSelectFilter
                properties={propertiesQuery.data ?? []}
                value={propertyFilter}
                onChange={setPropertyFilter}
                className="w-auto max-w-full sm:w-[180px]"
              />
              {canSend && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => setNewMessageOpen(true)}
                  data-testid="button-new-message"
                >
                  <Plus className="h-4 w-4" />
                  <span>New Message</span>
                </Button>
              )}
              {canSend && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => setBroadcastOpen(true)}
                  data-testid="button-broadcast"
                  data-tour="messages-broadcast"
                >
                  <Users className="h-4 w-4" />
                  <span>House Broadcast Message</span>
                </Button>
              )}
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2" data-testid="messages-header-help">
              <ExplainerVideoButton pageKey="messages" compactOnMobile />
              <GuideButton pageKey="messages" compactOnMobile />
            </div>
          </div>
          {/* Tabs */}
          <div className="flex items-center gap-0 border-t border-gray-100 px-4 overflow-x-auto">
            {TAB_ITEMS.map((t) => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`relative shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === t.id
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-${t.id}`}
                data-tour={`messages-${t.id}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {t.label}
                  {t.badge != null && t.badge > 0 && (
                    <span
                      className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                      data-testid={`tab-badge-${t.id}`}
                    >
                      {t.badge}
                    </span>
                  )}
                </span>
              </button>
            ))}
            {canAccessMessages && (
              <>
                {/* Vertical separator — Settings is an actions menu, not a tab */}
                <div
                  className="mx-2 h-5 w-px self-center bg-gray-200"
                  aria-hidden="true"
                  data-testid="separator-messages-settings"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === "rules" || activeTab === "canned"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                      data-testid="button-messages-settings"
                      data-tour="messages-settings"
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="menu-rules-canned">
                        <ScrollText className="mr-2 h-4 w-4 text-blue-500" />
                        Rules and Canned Responses
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem
                          onClick={() => switchTab("rules")}
                          data-testid="menu-rules"
                        >
                          <ScrollText className="mr-2 h-4 w-4 text-blue-500" />
                          Rules
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => switchTab("canned")}
                          data-testid="menu-canned"
                        >
                          <MessageSquareText className="mr-2 h-4 w-4 text-blue-500" />
                          Canned Responses
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuItem
                      onClick={() => {
                        setAutoResolveIsFirstVisit(false);
                        setAutoResolveOpen(true);
                      }}
                      data-testid="menu-auto-resolve"
                    >
                      <RotateCcw className="mr-2 h-4 w-4 text-blue-500" />
                      Auto-resolve old messages
                    </DropdownMenuItem>
                    {hasAutoDraft && (
                      <DropdownMenuItem
                        onClick={() => navigate(AUTO_DRAFT_BETA.href)}
                        data-testid="menu-auto-draft"
                      >
                        <Sparkles className="mr-2 h-4 w-4 text-blue-500" />
                        Auto Draft
                      </DropdownMenuItem>
                    )}
                    {hasQuoMessaging && (
                      <DropdownMenuItem
                        onClick={() => navigate("/app/messages/settings")}
                        data-testid="menu-messaging-provider"
                      >
                        <Plug className="mr-2 h-4 w-4 text-blue-500" />
                        Messaging provider
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuCheckboxItem
                      data-testid="menu-show-inactive-members"
                      checked={showInactiveMembers}
                      disabled={profileMutation.isPending}
                      onCheckedChange={(checked) => {
                        profileMutation.mutate(checked === true);
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Show Inactive Members
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
        <GuideNudgeBanner pageKey="messages" />

        {/* ── Unmatched SMS triage banner ── */}
        {unmatchedCount > 0 && (
          <div
            className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm"
            data-testid="banner-unmatched-sms"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="text-amber-900">
              <strong>{unmatchedCount}</strong> unmatched inbound SMS message{unmatchedCount !== 1 ? "s" : ""} need triage.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto h-7 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              asChild
              data-testid="button-triage-unmatched"
            >
              <Link href="/app/messages/unmatched-sms">Review</Link>
            </Button>
          </div>
        )}

        {/* ── Tab content ── */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === "waitingReplies" && (
            <WaitingRepliesTab
              propertyIds={scopedPropertyIds}
              onOpen={(memberId, chatId) => selectMember(memberId, chatId, true)}
            />
          )}
          {activeTab === "inbox" && (
            <InboxTab
              filtered={filtered}
              allInboxRows={inboxQuery.data ?? []}
              search={search}
              setSearch={setSearch}
              isContentSearching={isContentSearching}
              matchedChatIdByMember={matchedChatIdByMember}
              matchedSnippetByMember={matchedSnippetByMember}
              searchWords={deriveSearchWords(search)}
              searchMode={searchMode}
              setSearchMode={setSearchMode}
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
              searchSinceMs={searchSinceMs}
              groupFilter={groupFilter}
              propertyFilterKey={propertyFilter.join(",")}
              serverHasMore={
                activeTab === "inbox" && inboxServerHasMore &&
                !(scopedPropertyIds != null && scopedPropertyIds.length > 0)
              }
              serverCapReached={
                // Under a property scope the server caps at 500 too, and there
                // is no deeper page to request — so surface the "use search"
                // hint when that cap is actually hit, instead of silently
                // handing back a recency-truncated list (the exact thing the
                // property filter exists to avoid).
                scopedPropertyIds != null && scopedPropertyIds.length > 0
                  ? propertyScopedInboxQuery.data != null &&
                    propertyScopedInboxQuery.data.length >= INBOX_FETCH_MAX
                  : inboxServerCapReached
              }
              onRequestMore={requestMoreInbox}
              unresolvedOnly={unresolvedOnly}
              setUnresolvedOnly={setUnresolvedOnly}
              unresolvedTotal={unresolvedTotal}
              selectedMemberId={selectedMemberId}
              setSelectedMemberId={selectMember}
              focusChatId={focusChatId}
              focusThreadId={focusThreadId}
              focusPendingId={focusPendingId}
              canSend={canSend}
              toast={toast}
              inboxLoading={
                unresolvedOnly
                  ? unresolvedInboxQuery.isLoading
                  : scopedPropertyIds != null && scopedPropertyIds.length > 0
                    ? propertyScopedInboxQuery.isLoading
                    : inboxQuery.isLoading
              }
            />
          )}
          {activeTab === "houseChats" && <HouseChatsTab />}
          {activeTab === "awaiting" && (
            <PendingTab
              filter="awaiting"
              canManage={canManage}
              toast={toast}
            />
          )}
          {activeTab === "scheduled" && (
            <PendingTab
              filter="scheduled"
              canManage={canManage}
              toast={toast}
            />
          )}
          {activeTab === "rules" && (
            <RulesTab canManage={canManage} navigate={navigate} toast={toast} />
          )}
          {activeTab === "canned" && canAccessMessages && (
            <CannedResponsesTab canManage={canAccessMessages} toast={toast} />
          )}
        </div>
      </div>

      {/* Modals */}
      <NewMessageModal
        open={newMessageOpen}
        onOpenChange={setNewMessageOpen}
        onSent={(memberId) => {
          setSelectedMemberId(memberId);
          switchTab("inbox");
          try {
            sessionStorage.setItem(LAST_MEMBER_ID_KEY, String(memberId));
          } catch {}
          const params = new URLSearchParams(window.location.search);
          params.delete("cleaningProperty");
          params.delete("cleaningAssignment");
          params.set("memberId", String(memberId));
          params.set("tab", "inbox");
          // Remove legacy deep-link params so the URL stays clean,
          // consistent with what selectMember() does on row selection.
          params.delete("member");
          params.delete("chat");
          params.delete("thread");
          // Push a new history entry so the user can back out of the
          // freshly-opened conversation.
          navigate(`/app/messages?${params.toString()}`);
        }}
      />
      <MessageWholeHouseModal open={broadcastOpen} onOpenChange={setBroadcastOpen} />
      <BroadcastDeepLinkDialog />
      <AutoResolveDialog
        open={autoResolveOpen}
        onOpenChange={(next) => {
          setAutoResolveOpen(next);
          if (!next) setAutoResolveIsFirstVisit(false);
        }}
        showDismiss={autoResolveIsFirstVisit}
      />
    </AppLayout>
  );
}
